// Code-action: `Change "FORM" to "FROM"` — the quick fix for the
// structural-keyword-typo syntax diagnostic.
//
// The diagnostic already KNOWS the repair: the tree-sitter syntax witness only
// squiggles a word when it is Damerau-distance-1 from the single structural
// keyword its slot allows, and it says which one in its message. That knowledge
// used to die at the marker — Monaco's `IMarkerData` has no payload beyond
// `message`/`code`, no registered code action matched a `*-syntax` code, so the
// marker hover rendered "No quick fixes available" for a fully determined
// one-token edit.
//
// This fix re-reads the originating diagnostic through the shared message
// contract (`keywordTypoMessage.ts` — the witness BUILDS the message with the
// same module, so there is one format definition and no consumer-side regex
// drift) and rewrites exactly the squiggled span. `kind: 'quickfix'` + an edit
// on the marker's own range is what makes Monaco surface it in the hover's
// "Quick Fix…" affordance and under `editor.action.quickFix` (Cmd+. / Ctrl+.),
// not only in the standalone lightbulb menu.

import { parseKeywordTypoMessage } from '../../../intellisense/diagnostics/syntax/keywordTypoMessage';
import { SYNTAX_MARKER_CODES } from '../../../intellisense/diagnostics/syntax/syntaxMarker';
import type {
  CodeActionContext,
  CodeActionDiagnostic,
  CodeActionEdit,
  CodeActionRegistryEntry,
  SqlRange,
} from '../../types';

const ENTRY_ID = 'fix-keyword-typo' as const;

const SYNTAX_CODES = new Set<string>(SYNTAX_MARKER_CODES);

export const fixKeywordTypoEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'quickfix',
  title: 'Fix keyword typo',
  canApply: (ctx) => collectFix(ctx) !== null,
  buildEdits: (ctx) => {
    const fix = collectFix(ctx);
    return fix ? [fix] : [];
  },
};

function collectFix(ctx: CodeActionContext): CodeActionEdit | null {
  const diagnostic = findKeywordTypoDiagnostic(ctx);
  if (!diagnostic) return null;
  const typo = parseKeywordTypoMessage(diagnostic.message);
  if (!typo) return null;

  // Markers are eventually-consistent: the pass may have painted this one
  // before the last keystroke. Only offer the fix while the squiggled span
  // still holds the exact token the message reported.
  const { range } = diagnostic;
  if (ctx.sourceText.slice(range.startOffset, range.endOffset) !== typo.raw) return null;

  const replacement = matchKeywordCase(typo.keyword, typo.raw, ctx.sourceText, range);
  if (replacement === typo.raw) return null;

  return {
    id: ENTRY_ID,
    description: `Change "${typo.raw}" to "${replacement}"`,
    textEdits: [{ range, newText: replacement }],
  };
}

/** The first syntax-owned diagnostic overlapping the request range whose
 *  message is a keyword typo. Monaco pre-filters `ctx.diagnostics` to markers
 *  intersecting/touching the range; the explicit overlap test keeps the fix
 *  honest when it is called directly (unit tests, future callers). */
function findKeywordTypoDiagnostic(ctx: CodeActionContext): CodeActionDiagnostic | null {
  for (const diagnostic of ctx.diagnostics ?? []) {
    if (!SYNTAX_CODES.has(diagnostic.code)) continue;
    if (!rangesOverlap(diagnostic.range, ctx.range)) continue;
    if (parseKeywordTypoMessage(diagnostic.message)) return diagnostic;
  }
  return null;
}

function rangesOverlap(a: SqlRange, b: SqlRange): boolean {
  return a.startOffset <= b.endOffset && b.startOffset <= a.endOffset;
}

// ── Casing ──────────────────────────────────────────────────────────────────

// The witness always reports the keyword UPPERCASE, but rewriting `select *
// form t` into `select * FROM t` would fight the user's own convention. The
// surrounding code is the primary signal; the misspelled token's own shape is
// the tiebreak when the buffer holds no other keyword to learn from.
const CASE_SIGNAL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'INSERT', 'INTO', 'UPDATE', 'SET', 'DELETE',
  'VALUES', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'FULL', 'CROSS', 'ON',
  'GROUP', 'ORDER', 'BY', 'HAVING', 'LIMIT', 'UNION', 'AND', 'OR', 'NOT', 'AS',
  'DISTINCT', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'NULL', 'IS', 'IN',
]);

function matchKeywordCase(
  keyword: string,
  raw: string,
  source: string,
  exclude: SqlRange,
): string {
  const convention = documentKeywordCase(source, exclude);
  if (convention === 'upper') return keyword.toUpperCase();
  if (convention === 'lower') return keyword.toLowerCase();
  return applyTokenShape(keyword, raw);
}

/** `'upper'` / `'lower'` when the buffer's OTHER keywords lean one way, else
 *  `null` (no signal — mixed-case or keyword-free buffer). */
function documentKeywordCase(source: string, exclude: SqlRange): 'upper' | 'lower' | null {
  const wordRe = /[A-Za-z_][A-Za-z0-9_]*/g;
  let upper = 0;
  let lower = 0;
  for (let match = wordRe.exec(source); match; match = wordRe.exec(source)) {
    const word = match[0];
    const start = match.index;
    const end = start + word.length;
    // Never let the misspelled token vote on its own replacement.
    if (start < exclude.endOffset && exclude.startOffset < end) continue;
    if (!CASE_SIGNAL_KEYWORDS.has(word.toUpperCase())) continue;
    if (word === word.toUpperCase()) upper += 1;
    else if (word === word.toLowerCase()) lower += 1;
  }
  if (upper > lower) return 'upper';
  if (lower > upper) return 'lower';
  return null;
}

function applyTokenShape(keyword: string, raw: string): string {
  if (raw === raw.toLowerCase()) return keyword.toLowerCase();
  if (raw === raw.toUpperCase()) return keyword.toUpperCase();
  const isCapitalized =
    raw[0] === raw[0]?.toUpperCase() && raw.slice(1) === raw.slice(1).toLowerCase();
  if (isCapitalized) return keyword[0] + keyword.slice(1).toLowerCase();
  return keyword.toUpperCase();
}
